import { useAuthStore } from "@/lib/auth-store";
import { getDeviceId, getDeviceName } from "@/lib/device";
import type {
  ApiKey,
  AuthResponse,
  CreatedApiKey,
  RecentUsage,
  TimelinePoint,
  UsageSummary,
  User
} from "@/types/api";

type RequestOptions = RequestInit & {
  auth?: boolean;
};

const appBasePath = "";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const ERROR_TRANSLATIONS: Record<string, string> = {
  "Invalid token": "登录已过期，请重新登录",
  "Could not validate credentials": "登录已过期，请重新登录",
  "Not authenticated": "请先登录",
  "Token expired": "登录已过期，请重新登录",
  "Invalid credentials": "账号或密码不正确",
  "Email already registered": "该邮箱已注册",
  "Key not found": "密钥不存在",
};

function translateError(msg: string): string {
  return ERROR_TRANSLATIONS[msg] ?? msg;
}

/** FastAPI 的 detail 有三种形状：字符串（我们自己 raise 的）、
 *  对象数组（422 校验错误，每项形如 {loc, msg, type}）、或单个对象。
 *  原来这里把它的类型**断言**成 string——断言在运行时不校验，
 *  于是 422 的数组被当字符串拼接，用户看到的是 "[object Object],[object Object]"。
 *  这不是分享功能特有的，全站每一个 422 都长这样。 */
function readDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  const msgOf = (d: unknown): string | null =>
    d && typeof d === "object" && typeof (d as { msg?: unknown }).msg === "string"
      ? (d as { msg: string }).msg
      : null;
  if (Array.isArray(detail)) {
    const msgs = detail.map(msgOf).filter((m): m is string => Boolean(m));
    if (msgs.length) return msgs.join("；");
    // 一条 msg 都取不出来时也别把数组丢给字符串拼接
    return detail.length ? "请求参数有误" : "";
  }
  return msgOf(detail) ?? (detail ? "请求参数有误" : "");
}

async function readError(response: Response) {
  try {
    const data = (await response.json()) as { message?: string; error?: string; detail?: unknown };
    const raw = data.message || data.error || readDetail(data.detail) || "请求失败，请稍后重试";
    return translateError(raw);
  } catch {
    return translateError(response.statusText) || "请求失败，请稍后重试";
  }
}

function withBasePath(path: string) {
  if (/^https?:\/\//.test(path) || !path.startsWith("/")) return path;
  if (path === appBasePath || path.startsWith(`${appBasePath}/`)) return path;
  return `${appBasePath}${path}`;
}

/**
 * 续期用的"单飞"锁：并发请求同时撞上 401 时，全都 await 同一个 Promise，
 * 避免刷新风暴（多张 refresh 并发提交，服务端轮换后互相作废）。
 */
let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  refreshing ??= (async () => {
    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) throw new ApiError("未登录", 401);

      const r = await fetch(withBasePath("/api/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          refresh_token: refreshToken,
          device_id: getDeviceId(),
          device_name: getDeviceName(),
          platform: "web"
        })
      });
      if (!r.ok) throw new ApiError("登录已过期", 401);

      const data = (await r.json()) as { access_token: string; refresh_token?: string };
      const state = useAuthStore.getState();
      state.setSession({
        accessToken: data.access_token,
        // 服务端是滚动续期，会一并下发新的 refresh token
        refreshToken: data.refresh_token ?? refreshToken,
        user: state.user!
      });
      return data.access_token;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** 鉴权失败且续期无望：清干净本地缓存，否则会出现"侧栏显示已登录、请求全 401"的割裂 */
function forceLogout() {
  useAuthStore.getState().clearSession();
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const { auth = true, headers, body, ...rest } = options;
  const token = useAuthStore.getState().accessToken;
  const requestHeaders = new Headers(headers);

  if (body && !(body instanceof FormData) && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (auth && token) {
    requestHeaders.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(withBasePath(path), {
    ...rest,
    body,
    headers: requestHeaders
  });

  // access token 只活 15 分钟：先续期再重试一次，续不动才让用户重新登录
  if (response.status === 401 && auth && !retried) {
    try {
      await refreshAccessToken();
    } catch {
      forceLogout();
      throw new ApiError("登录已过期，请重新登录", 401);
    }
    return apiFetch<T>(path, options, true);
  }

  if (!response.ok) {
    throw new ApiError(await readError(response), response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * 供流式聊天复用：token 剩余不足 60 秒就先续期再发。
 * 流式请求发出后中途 401 很难挽救（SSE 已经建立），所以宁可提前续。
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const { accessToken, refreshToken } = useAuthStore.getState();
  if (!accessToken) return null;
  if (!refreshToken) return accessToken;

  let expMs = 0;
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1] ?? ""));
    expMs = (payload?.exp ?? 0) * 1000;
  } catch {
    return accessToken; // 解不出来就按原样发，让服务端判
  }

  if (expMs && expMs - Date.now() < 60_000) {
    try {
      return await refreshAccessToken();
    } catch {
      forceLogout();
      return null;
    }
  }
  return accessToken;
}

export { refreshAccessToken, forceLogout };

export type OAuthAuthorizeResponse = {
  authorize_url: string;
  /** 下面三个给网页内嵌二维码（微信 wxLogin.js）用，state 由服务端签发并同时写进 httpOnly cookie */
  appid: string;
  redirect_uri: string;
  state: string;
};

export type OAuthStartPayload = {
  provider: "wechat";
  credential: string;
  state: string;
};

export type OAuthStartResponse =
  | { status: "ok"; user: User; access_token: string; refresh_token: string }
  | { status: "need_bind"; bind_token: string };

export type OAuthBindPayload = {
  bind_token: string;
  phone: string;
  code: string;
  device_id?: string;
  device_name?: string;
  platform?: string;
};

export const authApi = {
  wechatAuthorize: () =>
    apiFetch<OAuthAuthorizeResponse>("/api/auth/oauth/wechat/authorize", {
      auth: false
    }),
  oauthStart: (payload: OAuthStartPayload) =>
    apiFetch<OAuthStartResponse>("/api/auth/oauth/start", {
      method: "POST",
      auth: false,
      body: JSON.stringify(payload)
    }),
  oauthBind: (payload: OAuthBindPayload) =>
    apiFetch<AuthResponse>("/api/auth/oauth/bind", {
      method: "POST",
      auth: false,
      body: JSON.stringify(payload)
    }),
  register: (payload: { email: string; password: string }) =>
    apiFetch<AuthResponse>("/api/auth/register", {
      method: "POST",
      auth: false,
      body: JSON.stringify(payload)
    }),
  login: (payload: { identifier: string; password: string }) =>
    apiFetch<AuthResponse>("/api/auth/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify(payload)
    }),
  sendSms: (phone: string) =>
    apiFetch<{ sent: boolean; retry_after: number }>("/api/auth/sms/send", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ phone })
    }),
  smsLogin: (phone: string, code: string) =>
    apiFetch<AuthResponse>("/api/auth/sms/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ phone, code })
    }),
  forgotPassword: (email: string) =>
    apiFetch<{ ok: boolean }>("/api/auth/password/forgot", { method: "POST", auth: false, body: JSON.stringify({ email }) }),
  resetPassword: (token: string, new_password: string) =>
    apiFetch<{ ok: boolean }>("/api/auth/password/reset", { method: "POST", auth: false, body: JSON.stringify({ token, new_password }) }),
  me: () => apiFetch<{ user: User }>("/api/auth/me"),
  // 后端按 refresh token 定位当前 session 吊销；不带就是 422，
  // 且只凭 access token 吊销会把用户其他设备一起登出
  logout: () =>
    apiFetch<void>("/api/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refresh_token: useAuthStore.getState().refreshToken ?? "" })
    }),
  sessions: () =>
    apiFetch<
      {
        id: string;
        // 客户端靠它认出"当前设备"——GET 里没有 refresh token，服务端算不出来
        device_id: string | null;
        device_name: string | null;
        platform: string | null;
        ip: string | null;
        last_used_at: string;
        created_at: string;
      }[]
    >("/api/auth/sessions"),
  revokeSession: (id: string) =>
    apiFetch<void>(`/api/auth/sessions/${id}`, { method: "DELETE" }),
  revokeOtherSessions: () =>
    apiFetch<void>("/api/auth/sessions/revoke_all", {
      method: "POST",
      body: JSON.stringify({ refresh_token: useAuthStore.getState().refreshToken ?? "" })
    }),
  // 连当前设备一起登出：不带 refresh_token，后端就找不到"当前 session"，
  // 于是不排除任何 family——这正是「登出所有设备」要的语义。
  // 与设备面板的 revokeOtherSessions（保留本机）是两个不同的动作，别混用。
  revokeAllSessions: () =>
    apiFetch<void>("/api/auth/sessions/revoke_all", {
      method: "POST",
      body: JSON.stringify({ refresh_token: "" })
    }),
  updatePrivacy: (allow_training: boolean) =>
    apiFetch<User>("/api/auth/privacy", {
      method: "PATCH",
      body: JSON.stringify({ allow_training })
    }),
  updateProfile: (nickname: string) =>
    apiFetch<User>("/api/auth/profile", { method: "PATCH", body: JSON.stringify({ nickname }) }),
  // 换绑走新号验证：码发到**新**号，且用途与登录码隔离，两边不能互相顶用
  sendPhoneChangeCode: (phone: string) =>
    apiFetch<{ sent: boolean; retry_after: number }>("/api/auth/phone/change/send", {
      method: "POST",
      body: JSON.stringify({ phone })
    }),
  changePhone: (phone: string, code: string) =>
    apiFetch<User>("/api/auth/phone/change", {
      method: "POST",
      body: JSON.stringify({ phone, code })
    }),
  identities: () =>
    apiFetch<{ provider: string; created_at: string }[]>("/api/auth/identities"),
  unbindIdentity: (provider: string) =>
    apiFetch<void>(`/api/auth/identities/${provider}`, { method: "DELETE" }),
  // 注销要当前手机号收到的验证码（走普通 sendSms，后端不校验 purpose）
  deleteAccount: (code: string) =>
    apiFetch<void>(`/api/auth/account?code=${encodeURIComponent(code)}`, { method: "DELETE" })
};

export const keysApi = {
  list: () => apiFetch<ApiKey[]>("/api/keys"),
  create: (name: string) =>
    apiFetch<CreatedApiKey>("/api/keys", {
      method: "POST",
      body: JSON.stringify({ name })
    }),
  revoke: (id: string) =>
    apiFetch<void>(`/api/keys/${id}`, {
      method: "DELETE"
    }),
  update: (id: string, name: string) =>
    apiFetch<import("@/types/api").ApiKey>(`/api/keys/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name })
    })
};

export const feedbackApi = {
  submit: (payload: {
    rating: "up" | "down";
    session_id?: string | null;
    message_id?: string | null;
    prompt_text?: string | null;
    response_text?: string | null;
    note?: string | null;
  }) =>
    apiFetch<{ id: number; rating: string }>("/api/feedback", {
      method: "POST",
      body: JSON.stringify(payload)
    })
};

export const usageApi = {
  summary: (period: "day" | "week" | "month") =>
    apiFetch<UsageSummary>(`/api/usage/summary?period=${period}`),
  timeline: (days: 7 | 30) => apiFetch<TimelinePoint[]>(`/api/usage/timeline?days=${days}`),
  recent: (limit = 100) => apiFetch<RecentUsage[]>(`/api/usage/recent?limit=${limit}`)
};
